'use client';
import { SvgHolaglowHand } from '@icons/oldIcons/SvgHolaglowHand';
import { Professional } from 'app/types/clinic';
import { doctorDisplayName } from 'app/utils/doctorName';
import { Button } from 'designSystem/Buttons/Buttons';
import { Flex } from 'designSystem/Layouts/Layouts';
import { Text } from 'designSystem/Texts/Texts';
import Image from 'next/image';

export default function ProfessionalCard({
  isDerma = false,
  professional,
  className = '',
  clickable = false,
  onClick,
  showMoreButton = false,
  href,
}: {
  isDerma?: boolean;
  professional: Professional;
  className?: string;
  clickable?: boolean;
  onClick?: (professional: Professional) => void;
  showMoreButton?: boolean;
  /** Ficha pública del médico (/equipo-medico/<slug>): la tarjeta pasa a ser un enlace. */
  href?: string;
}) {
  const handleClick = () => {
    if (clickable && onClick) {
      onClick(professional);
    }
  };

  return (
    <div className={`p-4 flex flex-col h-full ${className} professionalCard`}>
      <Flex
        layout="col-center"
        className={`flex-grow text-center shadow-centered-secondary rounded-3xl md:flex-row ${
          clickable ? 'cursor-pointer' : ''
        }`}
        onClick={handleClick}
      >
        <div
          className={`w-full bg-gradient 
        ${
          isDerma
            ? 'from-white from-0% via-derma-primary500/20 via-80% to-white '
            : 'from-white from-0% via-hg-secondary300/30 via-80% to-white pt-2'
        } rounded-tl-3xl rounded-tr-3xl md:rounded-tr-none md:rounded-bl-3xl overflow-hidden md:relative md:min-h-[350px]`}
        >
          <Image
            alt={professional.name}
            height={600}
            width={600}
            src={professional.urlPhoto}
            className={`w-full ${
              !isDerma ? 'scale-110 translate-y-[10%]' : ''
            } md:hidden`}
          />

          <Image
            alt={professional.name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            src={professional.urlPhoto}
            className={`hidden md:block ${!isDerma && 'pt-6'} object-cover`}
          />
        </div>

        <Flex
          layout="col-left"
          className="flex-grow md:h-full bg-white w-full rounded-bl-3xl rounded-br-3xl md:rounded-bl-none md:rounded-r-3xl overflow-hidden py-6 px-4 md:px-6 text-sm gap-2"
        >
          <SvgHolaglowHand
            className={`hidden md:block h-14 w-14 ${
              isDerma ? 'text-derma-primary500/20' : 'text-hg-secondary500'
            }`}
          />
          <Text
            className={`font-gtUltra font-light truncate overflow-hidden ${
              isDerma ? 'text-derma-primary' : 'text-hg-secondary'
            } text-xldr md:text-2xl w-full md:text-wrap`}
          >
            {doctorDisplayName(professional)}
          </Text>
          <Text>{professional.title}</Text>
          {professional.collegiateNumber && (
            <Text>Núm. Col. {professional.collegiateNumber}</Text>
          )}
          {href && (
            <a
              href={href}
              className={`mt-2 font-semibold underline underline-offset-4 ${
                isDerma ? 'text-derma-primary' : 'text-hg-secondary'
              }`}
            >
              Ver perfil
            </a>
          )}
          {showMoreButton && (
            <div className="mt-4">
              <Button
                type="primary"
                size="md"
                onClick={() => {
                  handleClick();
                }}
              >
                Saber más
              </Button>
            </div>
          )}
        </Flex>
      </Flex>
    </div>
  );
}
